import React, { useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { theme } from '../../theme/theme.js';
import './css/CommonModal.css';

const CommonModal = ({
  show,
  onHide,
  title = '',
  subtitle = '',
  size = 'lg',
  className = '',
  children,
  footer = null,
  showCloseButton = true,
  closeButtonText = 'Close',
  closeButtonVariant = 'secondary',
  onCloseButtonClick = null,
  backdrop = 'static',
  keyboard = true,
  centered = false,
  scrollable = false,
  headerIcon = null,
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
  closeButtonClassName = '',
  titleClassName = '',
  subtitleClassName = '',
  showBackdrop = true,
  onBackdropClick = null,
  customHeader = null,
  customFooter = null,
  ...props
}) => {
  const handleClose = () => {
    if (onCloseButtonClick) {
      onCloseButtonClick();
    } else {
      onHide();
    }
  };

  const handleHide = (e) => {
    // If backdrop is static and user clicked on backdrop, prevent closing
    if (backdrop === 'static' && e && e.target && e.target.classList.contains('modal')) {
      return;
    }
    
    // If custom backdrop click handler is provided, use it
    if (onBackdropClick && e && e.target && e.target.classList.contains('modal')) {
      onBackdropClick(e);
      return;
    }
    
    // Default behavior
    onHide();
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      console.log('Escape key pressed, key:', event.key, 'show:', show, 'keyboard:', keyboard);
      
      // Check if there's a child modal open (EditLayoutModal)
      const childModals = document.querySelectorAll('.modal.show');
      const hasChildModal = childModals.length > 1; // More than one modal is open
      
      if (event.key === 'Escape' && show && keyboard) {
        // If there's a child modal, don't close the parent
        if (hasChildModal) {
          console.log('Child modal detected, not closing parent modal');
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        
        event.preventDefault();
        event.stopPropagation();
        console.log('Escape key pressed, closing modal');
        onHide();
      } else {
        console.log('Conditions not met - show:', show, 'keyboard:', keyboard, 'key:', event.key);
      }
    };

    if (show) {
      document.addEventListener('keydown', handleEscapeKey, true);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [show, keyboard, onHide]);

  return (
    <Modal
      show={show}
      onHide={handleHide}
      size={size}
      className={`common-modal ${className}`}
      backdrop={backdrop}
      keyboard={keyboard}
      centered={centered}
      scrollable={scrollable}
      onEscapeKeyDown={onHide}
      {...props}
    >
      {/* Custom Header or Default Header */}
      {customHeader ? (
        customHeader
      ) : (
        <Modal.Header className={`common-modal-header ${headerClassName}`}>
          <div className="header-content">
            {headerIcon && (
              <div className="header-icon">
                {headerIcon}
              </div>
            )}
            <div className="header-text">
              {subtitle && (
                <div className={`modal-subtitle ${subtitleClassName}`}>
                  {subtitle}
                </div>
              )}
              <Modal.Title className={`modal-title ${titleClassName}`}>
                {title}
              </Modal.Title>
            </div>
          </div>
          {showCloseButton && (
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <i className="fas fa-times"></i>
            </button>
          )}
        </Modal.Header>
      )}

      {/* Modal Body */}
      <Modal.Body className={`common-modal-body ${bodyClassName}`}>
        {children}
      </Modal.Body>

      {/* Custom Footer or Default Footer */}
      {customFooter ? (
        customFooter
      ) : footer ? (
        <Modal.Footer className={`common-modal-footer ${footerClassName}`}>
          {footer}
        </Modal.Footer>
      ) : showCloseButton ? (
        <Modal.Footer className={`common-modal-footer ${footerClassName}`}>
          <Button
            variant={closeButtonVariant}
            onClick={handleClose}
            className={`close-button ${closeButtonClassName}`}
          >
            {closeButtonText}
          </Button>
        </Modal.Footer>
      ) : null}
    </Modal>
  );
};

export default CommonModal;
